import React, { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import Confetti from "react-confetti"
import { nextRound } from "./scattergoriesRoom"
import { LISTS } from "../../../data/scattergoriesLists"
import { Screen, SgButton, PlayerDot, C, F } from "./ScattergoriesUI"
import { playSound } from "../../../utils/ballparkSounds"

const RevealScreen = ({ room, code, playerId, isHost, setShowScores }) => {
  const r = room.lastResult
  const [showHistory, setShowHistory] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)

  // Get window size for Confetti
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 500,
    height: typeof window !== "undefined" ? window.innerHeight : 800,
  })

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight })
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  if (!r) return null

  const players = Object.entries(room.players)
  const lastRound = room.round >= room.totalRounds
  const listData = LISTS.find((l) => l.id === r.listId) || LISTS[0]

  const handleNext = async () => {
    playSound("click")
    await nextRound(code)
  }

  // Animation state for count-up
  const [animatedPlayers, setAnimatedPlayers] = useState(() => {
    return players.map(([pid, p]) => {
      const gained = r.scoresGained?.[pid] || 0
      return {
        pid,
        name: p.name,
        color: p.color,
        startScore: p.score - gained,
        currentAnimatedScore: p.score - gained,
        targetScore: p.score,
        gained,
      }
    })
  })

  const [isCounting, setIsCounting] = useState(true)

  useEffect(() => {
    let active = true
    const duration = 2000 // 2 seconds total animation time
    const steps = 15 // number of increments
    const stepDuration = duration / steps
    let currentStep = 0

    const timer = setInterval(() => {
      if (!active) return
      currentStep++

      setAnimatedPlayers((prev) => {
        const next = prev.map((ap) => {
          if (ap.currentAnimatedScore < ap.targetScore) {
            const diff = ap.targetScore - ap.startScore
            const nextVal = Math.min(
              ap.targetScore,
              Math.round(ap.startScore + (diff * currentStep) / steps)
            )
            return { ...ap, currentAnimatedScore: nextVal }
          }
          return ap
        })

        const allDone = next.every((ap) => ap.currentAnimatedScore === ap.targetScore)
        if (allDone) {
          clearInterval(timer)
          setIsCounting(false)
          const pointsGained = next.some((ap) => ap.gained > 0)
          if (pointsGained) {
            playSound("gameover")
            setShowConfetti(true)
          }
        }
        return next
      })
    }, stepDuration)

    return () => {
      active = false
      clearInterval(timer)
    }
  }, [])

  // Sort by currently animated scores to animate vertical ordering in real-time
  const sortedPlayers = [...animatedPlayers].sort(
    (a, b) => b.currentAnimatedScore - a.currentAnimatedScore
  )

  return (
    <Screen dark style={{ gap: 18, position: "relative", overflowX: "hidden", maxHeight: "100dvh", overflowY: "hidden", padding: "16px 20px 20px" }}>
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={150}
        />
      )}

      {/* Top Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div>
          <span style={{ fontSize: 13, fontWeight: 800, color: C.tan }}>
            Round {room.round} of {room.totalRounds} Recap
          </span>
        </div>
        <button
          onClick={() => {
            playSound("click")
            setShowScores(true)
          }}
          style={{
            background: C.darkCard,
            border: `1.5px solid ${C.muted}`,
            borderRadius: 12,
            padding: "4px 10px",
            fontFamily: F.display,
            fontWeight: 800,
            fontSize: 12,
            color: C.bg,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          🏆 Scores
        </button>
      </div>

      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 18, paddingRight: 4, margin: "4px 0" }}>
        {/* Big Letter Banner */}
        <div style={{ textAlign: "center", marginTop: 6, flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: 2, color: C.indigo }}>
            THE LETTER WAS
          </div>
          <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 80, lineHeight: 1, marginTop: 8, color: C.white }}>
            {r.letter}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.tan, marginTop: 10 }}>
            List Pack: {listData.name}
          </div>
        </div>

        {/* Leaderboard race list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: C.tan, fontFamily: F.display }}>
            CURRENT LEADERBOARD STANDINGS
          </div>
          
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <AnimatePresence>
              {sortedPlayers.map((ap, idx) => (
                <motion.div
                  key={ap.pid}
                  layout
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    background: C.darkCard,
                    borderRadius: 16,
                    padding: "12px 14px",
                    border: `1.5px solid ${ap.gained > 0 && isCounting ? `${C.indigo}80` : "transparent"}`,
                    boxShadow: "0 4px 6px rgba(0,0,0,0.15)",
                  }}
                >
                  <span style={{ fontWeight: 800, fontSize: 14, color: C.tan, width: 16 }}>
                    {idx + 1}
                  </span>
                  <PlayerDot color={ap.color} size={24} />
                  <span style={{ fontWeight: 800, fontSize: 15, color: C.white, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {ap.name} {ap.pid === playerId && " (You)"}
                  </span>

                  {ap.gained > 0 && (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        color: C.green,
                        marginRight: 6,
                        background: `${C.green}15`,
                        padding: "2px 6px",
                        borderRadius: 6,
                        whiteSpace: "nowrap",
                      }}
                    >
                      +{ap.gained} pt
                    </span>
                  )}

                  <span
                    style={{
                      fontFamily: F.display,
                      fontWeight: 900,
                      fontSize: 18,
                      color: ap.gained > 0 || ap.currentAnimatedScore > 0 ? C.indigo : C.muted,
                      minWidth: 50,
                      textAlign: "right",
                    }}
                  >
                    {ap.currentAnimatedScore} pts
                  </span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>

        {/* History Toggle */}
        {room.history && room.history.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <button
              onClick={() => {
                playSound("click")
                setShowHistory(!showHistory)
              }}
              style={{
                background: "transparent",
                border: "none",
                color: C.tan,
                fontFamily: F.display,
                fontWeight: 800,
                fontSize: 14,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                margin: "0 auto",
                outline: "none",
              }}
            >
              {showHistory ? "▼ Hide Round History" : "▶ View Round History"}
            </button>
            {showHistory && (
              <div
                style={{
                  marginTop: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  background: C.darkCard,
                  borderRadius: 16,
                  padding: 14,
                  maxHeight: 180,
                  overflowY: "auto",
                  border: `1.5px solid ${C.muted}`,
                }}
              >
                {room.history.map((h, idx) => {
                  const histList = LISTS.find((l) => l.id === h.listId) || LISTS[0]
                  return (
                    <div
                      key={idx}
                      style={{
                        borderBottom: idx < room.history.length - 1 ? `1px dashed ${C.muted}` : "none",
                        paddingBottom: idx < room.history.length - 1 ? 10 : 0,
                        fontSize: 13,
                        color: C.white,
                      }}
                    >
                      <div style={{ fontWeight: 800, color: C.indigo }}>
                        Round {h.round}: Letter {h.letter}
                      </div>
                      <div style={{ color: C.tan, fontSize: 12, marginTop: 2 }}>
                        List: {histList.name}
                      </div>
                      <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
                        {Object.entries(h.scoresGained).map(([pid, pts]) => (
                          <div key={pid} style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                            <span style={{ color: C.bg }}>{room.players[pid]?.name || "Player"}:</span>
                            <span style={{ fontWeight: 800, color: C.indigo }}>+{pts} pts</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Host Button / Guest Waiting message */}
      <div style={{ marginTop: "auto", flexShrink: 0 }}>
        {isHost ? (
          <SgButton
            onClick={handleNext}
            disabled={isCounting}
            style={{
              opacity: isCounting ? 0.5 : 1,
              cursor: isCounting ? "default" : "pointer",
            }}
          >
            {lastRound ? "Final Standings →" : "Next Round →"}
          </SgButton>
        ) : (
          <div style={{ textAlign: "center", fontWeight: 700, color: C.tan, fontFamily: F.display, padding: 12 }}>
            {isCounting ? "Tabulating round scores..." : "Waiting for the host to advance…"}
          </div>
        )}
      </div>
    </Screen>
  )
}

export default RevealScreen
